import {useTranslation} from "@/i18n/hooks";
import {Dialog, DialogActions, DialogDescription, DialogTitle} from "@/components/tailwind/dialog";
import {Button} from "@/components/tailwind/button";

export type I18nValues = {
    [key: string]: string | number | boolean | Date | null | undefined
};

export interface ConfirmationConfig {
    title: string;
    titleValues?: I18nValues;
    description: string;
    descriptionValues?: I18nValues;
    cancelButton: {
        text: string;
        ariaLabel: string;
        ariaLabelValues?: I18nValues;
    };
    confirmButton: {
        text: string;
        ariaLabel: string;
        ariaLabelValues?: I18nValues;
        color?: 'red' | 'orange' | 'blue' | 'green' | 'amber' | 'yellow' | 'lime' | 'emerald' | 'teal' | 'cyan' | 'sky' | 'indigo' | 'violet' | 'purple' | 'fuchsia' | 'pink' | 'rose' | 'zinc';
    };
}

export interface ConfirmationDialogProps {
    isOpen: boolean;
    onConfirm: (entityId?: string) => void;
    onCancel: () => void;
    config: ConfirmationConfig;
    entityId?: string;
    namespace?: "menu" | "common" | "login" | "users" | "members" | "orders" | "services" | "admin" | "errors" | "pricing";
    testIdPrefix?: string;
}

export default function ConfirmationDialog({
                                               isOpen,
                                               onConfirm,
                                               onCancel,
                                               config,
                                               entityId,
                                               namespace = "common",
                                               testIdPrefix
                                           }: ConfirmationDialogProps) {
    const {t} = useTranslation(namespace);

    if (!isOpen) return null;

    function handleSubmit() {
        onConfirm(entityId);
        onCancel();
    }

    return (
        <Dialog open={isOpen} onClose={onCancel} data-testid={testIdPrefix ? `${testIdPrefix}-dialog` : undefined}>
            <DialogTitle data-testid={testIdPrefix ? `${testIdPrefix}-header` : undefined}>
                {t(config.title, config.titleValues)}
            </DialogTitle>
            <DialogDescription data-testid={testIdPrefix ? `${testIdPrefix}-text` : undefined}>
                {t(config.description, config.descriptionValues)}
            </DialogDescription>
            <DialogActions>
                <Button
                    plain
                    onClick={onCancel}
                    aria-label={t(config.cancelButton.ariaLabel, config.cancelButton.ariaLabelValues)}
                >
                    {t(config.cancelButton.text)}
                </Button>
                <Button
                    onClick={handleSubmit}
                    color={config.confirmButton.color || "red"}
                    aria-label={t(config.confirmButton.ariaLabel, config.confirmButton.ariaLabelValues)}
                    data-testid={testIdPrefix ? `${testIdPrefix === 'cancel-order' ? 'confirm-cancel-button' : `${testIdPrefix}-confirm-button`}` : undefined}
                >
                    {t(config.confirmButton.text)}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
